import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type CpSettingField, type PropagateResult } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Toggle } from '@/components/ui/toggle';
import { ModesEditor } from '@/components/ModesEditor';
import { cn } from '@/lib/utils';
import { MODELS, PROVIDERS, slotsForMode, type Mode } from '@/lib/modes';

type ModeOption = { id: string; title: string };
// A tab is a list of groups, and a group is one card. Most settings are a card
// of their own, but some are only meaningful together — an alert's on/off
// switch and its threshold are one decision, not two — so the grouping is
// declared here as data and a single renderer covers both cases. A solo setting
// is just a group of one.
type SettingsGroup = { title?: string; keys: string[] };
type SettingsSection = { id: string; label: string; groups: SettingsGroup[] };

// Sugar for the common case: one card per key, titled by the field itself.
const solo = (...keys: string[]): SettingsGroup[] => keys.map((k) => ({ keys: [k] }));

// Any field whose key isn't listed here falls into an auto-appended "Other"
// tab, so new schema fields never silently disappear.
const SECTIONS: SettingsSection[] = [
  {
    id: 'modes',
    label: 'Modes & models',
    groups: solo('modes', 'defaultModeId', 'modelSwitch.enabled'),
  },
  {
    id: 'versions',
    label: 'Client versions',
    groups: solo('minimum_plugin_version', 'minimum_mobile_version'),
  },
  {
    id: 'compaction',
    label: 'Auto-compaction',
    groups: solo('compact_marker_min_input_tokens', 'compact_backstop_input_tokens'),
  },
  // The usage-limit switch moved to the tier (Tiers page → Usage allowance),
  // where it also picks the unit (dollars or minutes); only the internal margin
  // — which applies to the dollar meter alone — is still a fleet-global seed.
  {
    id: 'credits',
    label: 'Credits',
    groups: solo('credit_split_percent', 'credit_split_percent_by_provider'),
  },
  {
    id: 'control-plane',
    label: 'Control Plane',
    groups: [
      ...solo('alerts.recipients'),
      // Each alert is one card: the switch that arms it and the number that
      // tunes it. Split apart they read as unrelated settings.
      { title: 'Server unreachable', keys: ['alerts.health_enabled', 'alerts.health_failures'] },
      { title: 'Low memory', keys: ['alerts.memory_enabled', 'alerts.memory_min_available_percent'] },
      { title: 'Low disk', keys: ['alerts.disk_enabled', 'alerts.disk_max_used_percent'] },
      { title: 'Browser tools', keys: ['alerts.chrome_enabled', 'alerts.chrome_max_targets'] },
      ...solo('alerts.sustain_minutes'),
    ],
  },
];

// Badge shown on each card. 'cp_only' fields never leave the CP, so they get no
// propagate button (those are already gated on kind) and a badge that says so
// rather than implying a fleet-wide push.
const KIND_BADGE: Record<
  CpSettingField['kind'],
  { tone: 'warning' | 'neutral' | 'info'; label: string }
> = {
  seed: { tone: 'warning', label: 'seed for new tenants' },
  locked: { tone: 'neutral', label: 'locked (cp-only, propagated)' },
  cp_only: { tone: 'neutral', label: 'control plane only' },
  shared: { tone: 'info', label: 'shared (propagatable)' },
};

type ResolvedGroup = { key: string; title?: string; fields: CpSettingField[] };

export function SettingsPage() {
  const fields = useQuery({
    queryKey: ['cp-settings'],
    queryFn: () => api.get<{ fields: CpSettingField[] }>('/api/cp-settings'),
  });

  // The "Default mode ID" dropdown lists the saved modes. Both fields come
  // back in the same query, so we read the modes field's stored value (or its
  // built-in default when unsaved) to build the options.
  const modeOptions = useMemo<ModeOption[]>(() => {
    const modesField = fields.data?.fields.find((f) => f.key === 'modes');
    const raw = modesField?.cp_global ?? modesField?.default_value;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((m): m is Mode => !!m && typeof m === 'object')
      .map((m) => ({ id: String(m.id ?? ''), title: String(m.title ?? '') }));
  }, [fields.data]);

  // Resolve each section's groups from the fetched list; collect anything
  // ungrouped into a trailing "Other" tab so no field is hidden.
  const sections = useMemo(() => {
    const list = fields.data?.fields ?? [];
    const byKey = new Map(list.map((f) => [f.key, f]));
    const used = new Set<string>();
    const resolve = (groups: SettingsGroup[]): ResolvedGroup[] =>
      groups
        .map((g) => {
          const resolved = g.keys
            .map((k) => byKey.get(k))
            .filter((f): f is CpSettingField => !!f);
          resolved.forEach((f) => used.add(f.key));
          return { key: g.keys.join('+'), title: g.title, fields: resolved };
        })
        .filter((g) => g.fields.length > 0);

    const out = SECTIONS.map((s) => ({
      id: s.id,
      label: s.label,
      groups: resolve(s.groups),
    })).filter((s) => s.groups.length > 0);

    const other = list.filter((f) => !used.has(f.key));
    if (other.length) {
      out.push({ id: 'other', label: 'Other', groups: resolve(solo(...other.map((f) => f.key))) });
    }
    return out;
  }, [fields.data]);

  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const current = sections.find((s) => s.id === activeSection) ?? sections[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Global settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Fleet-wide defaults. Per-tenant overrides live on the tenant's detail page.
        </p>
      </div>

      {fields.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : !sections.length ? (
        <div className="text-sm text-slate-500">No settings.</div>
      ) : (
        <div className="space-y-6">
          <nav className="flex flex-wrap gap-1 border-b border-slate-200">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveSection(s.id)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  current?.id === s.id
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                )}
              >
                {s.label}
              </button>
            ))}
          </nav>
          {/* items-start: cards take their natural height. Forcing a shared
              row height lines the footers up but leaves a short card sitting
              in a large void, which reads as missing content. */}
          <div className="grid items-start gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {current?.groups.map((group) => (
              <GroupCard key={group.key} group={group} modeOptions={modeOptions} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// A json field (the modes editor, raw blobs) needs the full row; every other
// control is compact enough to sit in the grid.
function isWide(group: ResolvedGroup): boolean {
  return group.fields.some((f) => f.type === 'json');
}

function GroupCard({ group, modeOptions }: { group: ResolvedGroup; modeOptions: ModeOption[] }) {
  const qc = useQueryClient();
  const { fields } = group;
  const savedOf = (f: CpSettingField) => formatForEditor(f.cp_global, f.type);
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, savedOf(f)]))
  );
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever any of the group's server-side values change. The fields
  // array is rebuilt every render, so a value signature is the stable dep.
  const signature = fields.map((f) => `${f.key}=${savedOf(f)}`).join('|');
  useEffect(() => {
    setDrafts(Object.fromEntries(fields.map((f) => [f.key, savedOf(f)])));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const dirtyFields = fields.filter((f) => drafts[f.key] !== savedOf(f));
  const dirty = dirtyFields.length > 0;

  const save = useMutation({
    mutationFn: async () => {
      // Parse and validate everything before writing anything, so a bad value
      // in the second field can't leave the first one already saved.
      const payloads = dirtyFields.map((f) => {
        const parsed = parseFromEditor(drafts[f.key], f.type);
        if (f.key === 'modes') validateModes(parsed);
        return { key: f.key, value: parsed };
      });
      for (const { key, value } of payloads) {
        await api.put(`/api/cp-settings/${encodeURIComponent(key)}`, { value });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cp-settings'] });
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  // A leading boolean in a multi-field group is that group's master switch: it
  // moves to the header, where it reads as "is this alert on" rather than as
  // just another setting sitting above the others.
  const master = fields.length > 1 && fields[0].type === 'boolean' ? fields[0] : null;
  const rows = master ? fields.slice(1) : fields;
  const lead = master ?? fields[0];
  const soloUntitled = fields.length === 1 && !group.title;
  const title = group.title ?? fields[0].label;
  const armed = !master || drafts[master.key] === 'true';

  const kinds = [...new Set(fields.map((f) => f.kind))];
  const propagatable = fields.filter((f) => f.kind === 'shared' || f.kind === 'locked');
  const unsaved = fields.filter((f) => !f.cp_global_stored);

  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border transition-colors',
        dirty ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200 bg-white',
        isWide(group) && 'sm:col-span-2 xl:col-span-3'
      )}
    >
      <div className="flex-1 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium text-slate-900">{title}</h3>
            <code className="mt-0.5 block truncate font-mono text-[11px] text-slate-400" title={lead.key}>
              {lead.key}
            </code>
          </div>
          {master ? (
            <Toggle
              checked={drafts[master.key] === 'true'}
              onChange={(next) => setDrafts((d) => ({ ...d, [master.key]: String(next) }))}
              label={master.label}
              className="mt-0.5"
            />
          ) : (
            fields[0].type === 'boolean' && (
              <Toggle
                checked={drafts[fields[0].key] === 'true'}
                onChange={(next) => setDrafts((d) => ({ ...d, [fields[0].key]: String(next) }))}
                label={fields[0].label}
                className="mt-0.5"
              />
            )
          )}
        </div>

        <p className="mt-2 text-xs leading-relaxed text-slate-500">{lead.description}</p>

        {/* Dependent rows dim when the master switch is off — the threshold is
            still editable, it just isn't doing anything yet. */}
        {rows.length > 0 && (master || !soloUntitled || fields[0].type !== 'boolean') && (
          <div className={cn('mt-3 space-y-3', !armed && 'opacity-50')}>
            {rows.map((f) =>
              f.type === 'boolean' ? (
                <div key={f.key} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-slate-700">{f.label}</div>
                    <code className="font-mono text-[11px] text-slate-400">{f.key}</code>
                  </div>
                  <Toggle
                    checked={drafts[f.key] === 'true'}
                    onChange={(next) => setDrafts((d) => ({ ...d, [f.key]: String(next) }))}
                    label={f.label}
                  />
                </div>
              ) : (
                <div key={f.key}>
                  {!soloUntitled && (
                    <div className="mb-1">
                      <div className="text-xs font-medium text-slate-700">{f.label}</div>
                      <code className="font-mono text-[11px] text-slate-400">{f.key}</code>
                    </div>
                  )}
                  <Editor
                    type={f.type}
                    value={drafts[f.key] ?? ''}
                    onChange={(v) => setDrafts((d) => ({ ...d, [f.key]: v }))}
                    fieldKey={f.key}
                    unit={f.unit}
                    placeholder={f.placeholder}
                    modeOptions={modeOptions}
                  />
                  {!soloUntitled && f.description && (
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{f.description}</p>
                  )}
                </div>
              )
            )}
          </div>
        )}

        {error && (
          <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-200/70 px-4 py-2">
        {kinds.map((kind) => {
          const badge = KIND_BADGE[kind] ?? KIND_BADGE.shared;
          return (
            <Badge key={kind} tone={badge.tone} className="whitespace-nowrap">
              {badge.label}
            </Badge>
          );
        })}
        {unsaved.length > 0 && (
          <span
            className="text-[11px] text-slate-400"
            title="No saved CP-global value yet — the displayed value comes from the built-in default in settings-schema.ts. Saving inserts a row so the value is owned by the CP, not the code."
          >
            built-in default
          </span>
        )}
        {/* Save only appears once something actually changed, so a settled page
            is a wall of values rather than a wall of buttons. */}
        {dirty && (
          <div className="ml-auto flex items-center gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDrafts(Object.fromEntries(fields.map((f) => [f.key, savedOf(f)])))}
            >
              Revert
            </Button>
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {propagatable.map((f) => (
        <PropagateRow key={f.key} field={f} blocked={dirty} showKey={fields.length > 1} />
      ))}
    </div>
  );
}

function PropagateRow({
  field,
  blocked,
  showKey,
}: {
  field: CpSettingField;
  blocked: boolean;
  showKey: boolean;
}) {
  const qc = useQueryClient();
  const [forceOverride, setForceOverride] = useState(false);
  const [result, setResult] = useState<PropagateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const propagate = useMutation({
    mutationFn: (force: boolean) =>
      api.post<PropagateResult>(`/api/cp-settings/${encodeURIComponent(field.key)}/propagate`, {
        force,
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      qc.invalidateQueries({ queryKey: ['tenants'] });
      // Tenant detail pages cache settings per tenant id; invalidate broadly
      // since we don't know which tenants got their overrides cleared.
      qc.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="space-y-2 border-t border-slate-200/70 px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => propagate.mutate(field.kind === 'shared' ? forceOverride : false)}
          disabled={propagate.isPending || blocked}
          title={
            blocked
              ? 'Save the value before pushing it to tenants.'
              : field.kind === 'locked'
                ? 'Push to every tenant. Locked fields have no per-tenant overrides to worry about. Offline tenants come back as failures.'
                : forceOverride
                  ? 'Push to every tenant, including ones that customized the value in their own dashboard and ones with manual overrides (overrides are cleared). Tenants whose container is offline come back as failures.'
                  : 'Push to every tenant still on the fleet value. Tenants that customized the value in their own dashboard (live value differs from the last CP push, e.g. a testing model set) and tenants with manual overrides are left alone; offline tenants come back as failures.'
          }
        >
          {propagate.isPending
            ? 'Propagating…'
            : forceOverride && field.kind === 'shared'
              ? 'Force-push to all tenants'
              : `Push${showKey ? ` ${field.label.toLowerCase()}` : ''} to tenants`}
        </Button>
        {field.kind === 'shared' && (
          <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-slate-600 select-none">
            <input
              type="checkbox"
              checked={forceOverride}
              onChange={(e) => setForceOverride(e.target.checked)}
              className="cursor-pointer"
            />
            Also overwrite customized tenants
          </label>
        )}
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
      {result && <PropagationSummary result={result} />}
    </div>
  );
}

function Editor({
  type,
  value,
  onChange,
  fieldKey,
  unit,
  placeholder,
  modeOptions,
}: {
  type: CpSettingField['type'];
  value: string;
  onChange: (v: string) => void;
  fieldKey: string;
  unit?: string;
  placeholder?: string;
  modeOptions: ModeOption[];
}) {
  // Visual modes editor — keeps FieldCard's draft as a JSON string so the
  // existing parseFromEditor (JSON.parse) save path is untouched.
  if (fieldKey === 'modes') {
    let parsed: Mode[] = [];
    try {
      const v = JSON.parse(value);
      if (Array.isArray(v)) parsed = v as Mode[];
    } catch {
      // Malformed stored value — start from empty; the operator can rebuild.
    }
    return <ModesEditor value={parsed} onChange={(modes) => onChange(JSON.stringify(modes, null, 2))} />;
  }

  // Default mode picker — sourced from the saved modes.
  if (fieldKey === 'defaultModeId') {
    const known = modeOptions.some((o) => o.id === value);
    return (
      <div className="space-y-1.5">
        <Select value={value} onChange={(e) => onChange(e.target.value)}>
          {value === '' && (
            <option value="" disabled>
              Select a mode…
            </option>
          )}
          {!known && value !== '' && <option value={value}>{value} — not in modes list</option>}
          {modeOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.title ? `${o.title} (${o.id})` : o.id}
            </option>
          ))}
        </Select>
        {!known && value !== '' && (
          <p className="text-xs text-amber-700">
            This id isn't in the current modes — save the modes above first, or pick one here.
          </p>
        )}
      </div>
    );
  }

  // The unit sits beside the box rather than inside it: a suffix like
  // "% available" is too long to overlay without hand-tuned padding, and
  // "Alert after [3] failed checks" reads as a sentence.
  if (type === 'number') {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-24"
        />
        {unit && <span className="text-xs text-slate-500">{unit}</span>}
      </div>
    );
  }
  if (type === 'string') {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        {unit && <span className="shrink-0 text-xs text-slate-500">{unit}</span>}
      </div>
    );
  }
  // json
  return (
    <div>
      <Label htmlFor={`field-${fieldKey}`}>JSON</Label>
      <Textarea
        id={`field-${fieldKey}`}
        rows={10}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="font-mono text-xs"
      />
    </div>
  );
}

function PropagationSummary({ result }: { result: PropagateResult }) {
  if (!result.endpoint_built) {
    return (
      <div className="text-xs bg-amber-50 text-amber-800 rounded-md px-3 py-2 border border-amber-200">
        <b>Manual push needed.</b> Tenant orchestrators don't yet expose the{' '}
        <code className="font-mono">PATCH /admin/settings</code> endpoint, so this value lives in
        the CP only. Rebuild the tenant containers off a newer orchestrator image, or SSH to each
        host and update <code className="font-mono">settings.json</code> by hand.
      </div>
    );
  }
  const skipped = result.skipped ?? [];
  return (
    <div className="text-xs space-y-1">
      <div>
        Propagated to {result.ok} / {result.attempted} tenant
        {result.attempted === 1 ? '' : 's'}.
        {result.failed > 0 && (
          <span className="text-red-600 ml-2">({result.failed} failed)</span>
        )}
        {skipped.length > 0 && (
          <span className="text-amber-700 ml-2">({skipped.length} skipped)</span>
        )}
        {result.overrides_cleared > 0 && (
          <span className="text-slate-600 ml-2">
            ({result.overrides_cleared} manual override
            {result.overrides_cleared === 1 ? '' : 's'} cleared)
          </span>
        )}
      </div>
      {result.failed > 0 && (
        <ul className="text-xs text-red-700 space-y-0.5">
          {result.results
            .filter((r) => !r.ok)
            .slice(0, 10)
            .map((r) => (
              <li key={r.tenant_id} className="font-mono">
                {r.tenant_id} — {r.reason || `HTTP ${r.status}`}
              </li>
            ))}
        </ul>
      )}
      {skipped.length > 0 && (
        <div className="text-amber-700">
          Left alone — value customized in the tenant's own dashboard (
          <code className="font-mono">customized</code>) or live settings unreadable (
          <code className="font-mono">live_unreadable</code>). Re-run with "Also overwrite…" to
          force-push them:
          <ul className="space-y-0.5 mt-0.5">
            {skipped.slice(0, 10).map((s) => (
              <li key={s.tenant_id} className="font-mono">
                {s.tenant_id} — {s.reason}
              </li>
            ))}
            {skipped.length > 10 && <li>… and {skipped.length - 10} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatForEditor(value: unknown, type: CpSettingField['type']): string {
  if (type === 'json') return JSON.stringify(value ?? null, null, 2);
  if (type === 'boolean') return value === true ? 'true' : 'false';
  if (value == null) return '';
  return String(value);
}

function parseFromEditor(raw: string, type: CpSettingField['type']): unknown {
  if (type === 'json') return JSON.parse(raw);
  if (type === 'boolean') return raw === 'true';
  if (type === 'number') {
    const n = Number(raw);
    if (Number.isNaN(n)) throw new Error('Not a valid number');
    return n;
  }
  return raw;
}

// Submit guard: at least one mode, every mode titled, and every (auto-filled)
// provider slot references a known model. Both slots are auto-filled by
// slotsForMode so operators don't have to enter a model per provider manually.
function validateModes(parsed: unknown): void {
  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new Error('At least one mode is required.');
  }
  const known = new Set(MODELS.map((m) => m.id));
  for (const m of parsed) {
    if (!m || typeof m !== 'object' || !String((m as Mode).title ?? '').trim()) {
      throw new Error('Every mode needs a title.');
    }
    const slots = slotsForMode(m as Mode);
    for (const provider of PROVIDERS) {
      const model = slots[provider]?.model;
      if (model && !known.has(model)) {
        throw new Error(`Unknown ${provider} model "${model}" in mode "${String((m as Mode).title)}".`);
      }
    }
  }
}
